import type { Category, FloorSpace, FoodItem, TableArea } from '../types'
import { UNCATEGORIZED_CATEGORY_ID } from './categoryStorage'

/**
 * Flat list from MenuPage is ordered by category sections; assign per-category sortIndex
 * in traversal order (first item in a category block gets 0, then 1, …).
 */
export function assignPerCategorySortIndices(items: FoodItem[]): FoodItem[] {
  let lastKey: string | null = null
  let idx = 0
  return items.map((item) => {
    const key = item.categoryId ?? UNCATEGORIZED_CATEGORY_ID
    if (key !== lastKey) {
      lastKey = key
      idx = 0
    } else {
      idx += 1
    }
    return { ...item, sortIndex: idx }
  })
}

const MISSING_SORT = 1_000_000

/** Sort categories by sortIndex (missing last), then name. */
export function sortCategoriesByIndex(categories: Category[]): Category[] {
  return [...categories].sort(
    (a, b) =>
      (a.sortIndex ?? MISSING_SORT) - (b.sortIndex ?? MISSING_SORT) ||
      a.name.localeCompare(b.name),
  )
}

/** Sort table areas by sortIndex (missing last), then name. */
export function sortTableAreasByIndex(areas: TableArea[]): TableArea[] {
  return [...areas].sort(
    (a, b) =>
      (a.sortIndex ?? MISSING_SORT) - (b.sortIndex ?? MISSING_SORT) ||
      a.name.localeCompare(b.name),
  )
}

/** Sort floor spaces by sortIndex (missing last), then name. */
export function sortFloorSpacesByIndex(spaces: FloorSpace[]): FloorSpace[] {
  return [...spaces].sort(
    (a, b) =>
      (a.sortIndex ?? MISSING_SORT) - (b.sortIndex ?? MISSING_SORT) ||
      a.name.localeCompare(b.name),
  )
}

/**
 * Order menu items: walk categories in order, append items in that category sorted by sortIndex.
 */
export function sortMenuItemsByCategoryOrder(
  items: FoodItem[],
  categories: Category[],
): FoodItem[] {
  const byCat = new Map<string, FoodItem[]>()
  for (const item of items) {
    const key = item.categoryId ?? UNCATEGORIZED_CATEGORY_ID
    const list = byCat.get(key) ?? []
    list.push(item)
    byCat.set(key, list)
  }
  for (const [, list] of byCat) {
    list.sort(
      (a, b) =>
        (a.sortIndex ?? MISSING_SORT) - (b.sortIndex ?? MISSING_SORT) ||
        a.name.localeCompare(b.name),
    )
  }
  const out: FoodItem[] = []
  const seen = new Set<string>()
  for (const c of categories) {
    const list = byCat.get(c.id)
    if (list) {
      out.push(...list)
      seen.add(c.id)
    }
  }
  for (const [key, list] of byCat) {
    if (!seen.has(key)) out.push(...list)
  }
  return out
}
